/* ============================================================
   Tiny in-memory cache for list screens (courses, assessments,
   enrollments, trainings).

   Goal: when a user navigates away from a list and comes back,
   show the previously-loaded rows instantly instead of re-hitting
   a slow API. The cache is only invalidated when the data actually
   changes (add / update / delete), so a fresh fetch happens only
   when needed.

   Usage in a list screen:
     import { getCache, setCache, clearCache, clearCacheByPrefix } from '../ui/listCache';

     // Simple list (no pagination):
     useEffect(() => {
       const cached = getCache('assessments');
       if (cached) { setTableData(cached); return; }
       fetchRows();
     }, []);

     // Paginated list — key per query (see buildCourseListCacheKey):
     const key = buildCourseListCacheKey(userId, { page, size, sortBy, sortDir, keyword });
     const cached = getCache(key);
     if (cached) { applyPage(cached); return; }

     // after a successful add/update/delete:
     clearCacheByPrefix(`courses:${userId}:`);
     fetchRows();

   The cache lives for the lifetime of the SPA session (module
   scope). It is intentionally not persisted to storage so it can
   never go stale across logins.
   ============================================================ */

const store = new Map();

export const getCache = (key) => (store.has(key) ? store.get(key) : null);

export const setCache = (key, data) => { store.set(key, data); };

export const clearCache = (key) => {
  if (key == null) store.clear();
  else store.delete(key);
};

/** Removes every entry whose key starts with `prefix` (e.g. `courses:42:`). */
export const clearCacheByPrefix = (prefix) => {
  if (!prefix) return;
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) store.delete(key);
  }
};
